import { Suspense, useState, useEffect, useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { CardSummary } from '../../types/cards';
import { CardPackInteraction, CardRevealState, getCardPositionFor2D } from './CardPackInteraction';
import { ParticleSystem } from './ParticleSystem';
import { Card } from '../inventory/Card';
import { isMobile, getOptimalDPR } from '../../utils/deviceDetection';
import './GachaAnimation.css';

interface GachaAnimationProps {
  cards: CardSummary[];
  onComplete?: () => void;
  onSkip?: () => void;
}

// 카메라 애니메이션 컴포넌트
function AnimatedCamera({ 
  phase
}: { 
  phase: 'pack-appear' | 'pack-interaction' | 'cards-reveal' | 'complete';
}) {
  const { camera } = useThree();
  const defaultPosition = useRef([0, 0.2, 4.5] as const);
  
  useFrame(() => {
    if (phase === 'cards-reveal' || phase === 'complete') {
      // 카드 등장 시: 화면 중앙에 포커스
      const targetX = 0;
      const targetY = 0.2;
      const targetZ = 4.5; // 더 가깝게
      
      // 부드러운 이동
      camera.position.x += (targetX - camera.position.x) * 0.08;
      camera.position.y += (targetY - camera.position.y) * 0.08;
      camera.position.z += (targetZ - camera.position.z) * 0.08;
      
      // 화면 중앙을 바라보기
      camera.lookAt(0, 0, 0);
    } else {
      // 기본 위치
      const [defX, defY, defZ] = defaultPosition.current;
      camera.position.x += (defX - camera.position.x) * 0.05;
      camera.position.y += (defY - camera.position.y) * 0.05;
      camera.position.z += (defZ - camera.position.z) * 0.05;
      camera.lookAt(0, 0, 0);
    }
  });
  
  return null;
}

export function GachaAnimation({ cards, onComplete, onSkip }: GachaAnimationProps) {
  const [phase, setPhase] = useState<'pack-appear' | 'pack-interaction' | 'cards-reveal' | 'complete'>('pack-appear');
  const [canSkip, setCanSkip] = useState(false);
  const [cardStates, setCardStates] = useState<Map<number, CardRevealState>>(new Map());
  const [currentRevealingIndex, setCurrentRevealingIndex] = useState(-1);

  useEffect(() => {
    // 1초 후 스킵 가능
    const timer = setTimeout(() => setCanSkip(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // 카드팩 등장 애니메이션 후 인터랙션 단계로 전환
    if (phase === 'pack-appear') {
      const timer = setTimeout(() => {
        setPhase('pack-interaction');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  const handleRevealComplete = () => {
    setPhase('complete');
    setTimeout(() => onComplete?.(), 1500);
  };

  const handleSkip = () => {
    if (canSkip && onSkip) {
      onSkip();
    }
  };

  // WebGL 컨텍스트 최적화 설정
  const webglConfig = useMemo(() => {
    const mobile = isMobile();
    return {
      antialias: !mobile, // 모바일에서는 antialias 비활성화
      alpha: true,
      powerPreference: 'high-performance' as const,
      precision: mobile ? 'mediump' as const : 'highp' as const, // 모바일에서 정밀도 낮춤
      stencil: false, // 사용 안 하면 비활성화
      depth: true,
      logarithmicDepthBuffer: false, // 모바일에서 비활성화
      preserveDrawingBuffer: false, // 성능 향상
    };
  }, []);

  const optimalDPR = useMemo(() => getOptimalDPR(), []);

  if (cards.length === 0) return null;

  const highestGradeCard = cards.reduce((prev, curr) => {
    const gradeOrder: Record<string, number> = { SS: 6, S: 5, A: 4, B: 3, C: 2, D: 1 };
    return gradeOrder[curr.grade] > gradeOrder[prev.grade] ? curr : prev;
  }, cards[0]);

  return (
    <div className="gacha-animation-container">
      <Canvas
        gl={webglConfig}
        dpr={optimalDPR}
        performance={{ min: 0.5 }} // 성능이 낮으면 자동으로 품질 낮춤
        className="gacha-canvas"
      >
        <PerspectiveCamera makeDefault position={[0, 0.2, 4.5]} fov={60} />
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} />
        <pointLight position={[-5, -5, -5]} intensity={0.5} />
        <pointLight position={[0, 5, 0]} intensity={0.8} />

        <Suspense fallback={null}>
          <AnimatedCamera phase={phase} />
          {phase === 'pack-interaction' || phase === 'cards-reveal' ? (
            <CardPackInteraction
              cards={cards}
              onRevealComplete={handleRevealComplete}
              onPackOpened={() => setPhase('cards-reveal')}
              onCardStateChange={setCardStates}
              onCurrentRevealingIndexChange={setCurrentRevealingIndex}
            />
          ) : null}
          <ParticleSystem 
            phase={phase === 'complete' ? 'highlight' : phase === 'pack-interaction' ? 'reveal' : 'intro'} 
            highestGrade={highestGradeCard.grade} 
          />
        </Suspense>

        <OrbitControls
          enableZoom={false}
          enablePan={false}
          enableRotate={false}
          autoRotate={false}
        />
      </Canvas>

      {canSkip && phase !== 'complete' && (
        <button className="gacha-skip-button" onClick={handleSkip}>
          스킵
        </button>
      )}

      <div className="gacha-overlay">
        {phase === 'pack-appear' && (
          <div className="phase-text">모래 폭풍이 일어났습니다!</div>
        )}
        {phase === 'pack-interaction' && (
          <div className="phase-text">모래 폭풍이 강해지고 있습니다...</div>
        )}
        {phase === 'cards-reveal' && (
          <div className="phase-text">카드 소환!</div>
        )}
      </div>

      {/* 2D 카드 렌더링 */}
      <div className="gacha-cards-overlay">
        {cards.map((card, index) => {
          const state = cardStates.get(index); // 인덱스로 조회
          if (!state || state.phase === 'hidden') return null;

          const position = getCardPositionFor2D(card, index, cards.length, state);
          // 'appearing' 단계부터 앞면 표시 (뒷면 → 앞면 전환)
          const isRevealed = state.phase === 'appearing' || state.phase === 'moving' || state.phase === 'placed';
          const isHighlighted = currentRevealingIndex === index && state.phase === 'appearing';


          return (
            <div
              key={`card-${card.id}-${index}`}
              className="gacha-card-reveal-wrapper"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${position.scale}) rotate(${position.rotation}deg)`,
                opacity: position.opacity,
                transition: 'transform 0.1s ease-out, opacity 0.1s ease-out',
                zIndex: isHighlighted ? 1002 : 1001,
              }}
            >
              <div 
                className={`gacha-card-reveal ${isHighlighted ? 'gacha-card-highlighted' : ''}`}
              >
                <Card
                  card={card}
                  showBack={true}
                  isFlipped={isRevealed}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

